// RADIUS Servers: /pam/radius/servers — one row per FreeRADIUS server running the AuthNull
// bridge, and whether it is alive (each bridge sends a heartbeat every 60 seconds).
// API: authnull-service internal/radius/routes.go (ListServers, CreateServer, RenameServer,
// DeleteServer) through service/radiusService.js. Replaces the Connect FreeRADIUS page
// (/pam/radius/onboard, which redirects here): its steps open after Add RADIUS server.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Input, Modal, Table, Tooltip, message } from "antd";
import { Check, CircleAlert, CircleCheck, Plus, RefreshCw, X } from "lucide-react";

import {
  CountChip,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  StatusPill,
  TableCard,
  anTableProps,
  fmtDate,
  iconButtonStyle,
} from "../../../design-system/tableUi";
import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import { FieldLabel, Note } from "../ssh-proxy/sshProxyUi";
import {
  createRadiusServer,
  deleteRadiusServer,
  listRadiusServers,
  radiusErrorText,
  renameRadiusServer,
} from "../../../service/radiusService";
import RadiusServerSetup from "../../../components/radius/onboardRadiusDevices";
import {
  STATUS_LEGEND,
  deleteBlockedNote,
  healthChecks,
  inactiveHint,
  lastHeartbeatText,
  lastSeenMs,
  nameTakenMessage,
  neverConnected,
  parseHealth,
  sentence,
  serverNameError,
  serverStatusView,
  sourceIpError,
} from "./radiusServerModel";

// Heartbeats come every 60 s and a server goes Inactive after 3 min: re-read every 30 s.
const REFRESH_MS = 30000;
const DRAWER_WIDTH = "min(36rem, 100vw)";

const FieldError = ({ children }) =>
  children ? <div style={{ color: AN_COLORS.danger, fontSize: "0.78rem", marginTop: "0.3rem" }}>{children}</div> : null;

// ─── Details panel ───────────────────────────────────────────────────────────

function DetailRow({ label, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "8rem minmax(0, 1fr)", gap: "0.75rem", padding: "0.55rem 0", borderBottom: `1px solid ${AN_COLORS.line2}` }}>
      <span style={{ fontSize: "0.8rem", color: AN_COLORS.muted }}>{label}</span>
      <span style={{ fontSize: "0.85rem", color: AN_COLORS.ink, minWidth: 0, overflowWrap: "anywhere" }}>{children}</span>
    </div>
  );
}

function ServerDetails({ server, now, onDownload }) {
  const status = serverStatusView(server);
  const hint = inactiveHint(server, now);
  const health = parseHealth(server);
  const checks = healthChecks(server);
  const seen = lastSeenMs(server);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <StatusPill tone={status.tone} label={status.label} />
        {server.status === "degraded" && <span style={{ fontSize: "0.8rem", color: AN_COLORS.muted }}>A check failed in the last heartbeat.</span>}
      </div>
      {hint && (
        <div style={{ marginBottom: "1rem" }}>
          <Note tone="warn" icon={<CircleAlert size={15} />}>
            {hint}
          </Note>
        </div>
      )}

      <DetailRow label="Source IP">
        <span style={{ fontFamily: AN_MONO, fontSize: "0.8rem" }}>{server.sourceIp || "—"}</span>
      </DetailRow>
      <DetailRow label="Host">{health.hostname || "—"}</DetailRow>
      <DetailRow label="Last heartbeat">
        {neverConnected(server) ? "Never connected" : `${lastHeartbeatText(server, now)} · ${fmtDate(seen)}`}
      </DetailRow>
      <DetailRow label="Devices">{Number(server.devicesUsing) || 0}</DetailRow>
      <DetailRow label="Bridge version">
        <span style={{ fontFamily: AN_MONO, fontSize: "0.8rem" }}>{server.bridgeVersion || health.bridgeVersion || "—"}</span>
      </DetailRow>

      <div style={{ fontSize: "0.85rem", fontWeight: 600, margin: "1.4rem 0 0.5rem" }}>Checks in the last heartbeat</div>
      {checks.length === 0 ? (
        <div style={{ fontSize: "0.8rem", color: AN_COLORS.muted }}>No heartbeat yet.</div>
      ) : (
        <div style={{ border: `1px solid ${AN_COLORS.line}`, borderRadius: "0.5rem", overflow: "hidden" }}>
          {checks.map((c, i) => (
            <div
              key={c.name}
              style={{
                display: "grid",
                gridTemplateColumns: "1.2rem minmax(0, 1fr)",
                gap: "0.6rem",
                padding: "0.6rem 0.75rem",
                borderTop: i ? `1px solid ${AN_COLORS.line2}` : "none",
                background: c.ok ? "transparent" : "var(--an-warnBg)",
              }}
            >
              <span aria-label={c.ok ? "Passed" : "Failed"} style={{ color: c.ok ? AN_COLORS.success : AN_COLORS.warn, marginTop: "0.1rem" }}>
                {c.ok ? <CircleCheck size={16} /> : <CircleAlert size={16} />}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 500, color: AN_COLORS.ink }}>{c.label}</div>
                {c.detail && (
                  <div style={{ fontSize: "0.78rem", color: AN_COLORS.muted, overflowWrap: "anywhere", marginTop: "0.1rem" }}>{c.detail}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Button style={{ marginTop: "1.4rem" }} onClick={onDownload}>
        Download app.env
      </Button>
    </div>
  );
}

// ─── Add RADIUS server ───────────────────────────────────────────────────────

function AddServerForm({ servers, onCreated }) {
  const [name, setName] = useState("");
  const [sourceIp, setSourceIp] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const submit = () => {
    const next = { name: serverNameError(name, servers), sourceIp: sourceIpError(sourceIp), form: "" };
    setErrors(next);
    if (next.name || next.sourceIp) return;
    setSaving(true);
    createRadiusServer({
      name: name.trim(),
      type: "freeradius",
      sourceIp: sourceIp.trim(),
      onSuccess: (server) => {
        setSaving(false);
        // The API answers with the new row; fall back to what was sent if a field is missing.
        onCreated({ name: name.trim(), sourceIp: sourceIp.trim(), status: "never_connected", devicesUsing: 0, ...(server || {}) });
      },
      onFailure: (err) => {
        setSaving(false);
        if (err?.response?.status === 409) setErrors({ name: nameTakenMessage(name) });
        else setErrors({ form: sentence(radiusErrorText(err, "Could not add the server.")) });
      },
    });
  };

  const onEnter = (e) => e.key === "Enter" && submit();

  return (
    <div>
      <div style={{ fontSize: "0.85rem", color: AN_COLORS.muted, marginBottom: "1rem", lineHeight: 1.5 }}>
        Register your FreeRADIUS server. The next screen gives you its app.env and the install command.
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <FieldLabel required>Name</FieldLabel>
        <Input
          autoFocus
          value={name}
          status={errors.name ? "error" : undefined}
          placeholder="radius-lab"
          maxLength={128}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onEnter}
        />
        <FieldError>{errors.name}</FieldError>
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <FieldLabel required hint="IPv4">
          Source IP
        </FieldLabel>
        <Input
          value={sourceIp}
          status={errors.sourceIp ? "error" : undefined}
          placeholder="10.10.0.20"
          onChange={(e) => setSourceIp(e.target.value)}
          onKeyDown={onEnter}
          style={{ fontFamily: AN_MONO }}
        />
        <FieldError>{errors.sourceIp}</FieldError>
        <div style={{ fontSize: "0.78rem", color: AN_COLORS.muted, marginTop: "0.3rem" }}>The address the server&apos;s heartbeats come from.</div>
      </div>
      <div style={{ fontSize: "0.8rem", color: AN_COLORS.muted, marginBottom: "1rem" }}>Type: FreeRADIUS</div>
      {errors.form && (
        <div style={{ marginBottom: "1rem" }}>
          <Note tone="danger">{errors.form}</Note>
        </div>
      )}
      <Button type="primary" loading={saving} onClick={submit}>
        Add server
      </Button>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function RadiusServersPage() {
  const [msg, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();

  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [now, setNow] = useState(Date.now());
  // Side panels: details of a server; add (server null until created, then its setup steps);
  // setup of an existing server (Download app.env).
  const [detailsId, setDetailsId] = useState(null);
  const [adding, setAdding] = useState(null); // null closed · { server: null | created }
  const [setupFor, setSetupFor] = useState(null);
  // Inline rename.
  const [renaming, setRenaming] = useState(null); // { id, name, error, saving }
  const loaded = useRef(false);

  const load = useCallback(
    (quiet) => {
      if (!quiet) setLoading(true);
      listRadiusServers({
        onSuccess: (data) => {
          setServers(Array.isArray(data) ? data : []);
          setLoadError("");
          setLoading(false);
          setNow(Date.now());
          loaded.current = true;
        },
        onFailure: (err) => {
          setLoading(false);
          // A background refresh that fails keeps the rows it has.
          if (quiet && loaded.current) return;
          const text = sentence(radiusErrorText(err, "Could not load the RADIUS servers."));
          setLoadError(text);
          msg.error(text);
        },
      });
    },
    [msg],
  );

  useEffect(() => {
    load(false);
    const refresh = setInterval(() => load(true), REFRESH_MS);
    // The relative times tick between refreshes.
    const tick = setInterval(() => setNow(Date.now()), 10000);
    return () => {
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, [load]);

  const details = useMemo(() => servers.find((s) => s.id === detailsId) || null, [servers, detailsId]);
  const activeCount = servers.filter((s) => serverStatusView(s).active).length;

  // ── Rename ──
  const startRename = (s) => setRenaming({ id: s.id, name: s.name || "", error: "", saving: false });
  const saveRename = () => {
    if (!renaming || renaming.saving) return;
    const current = servers.find((s) => s.id === renaming.id);
    const error = serverNameError(renaming.name, servers, renaming.id);
    if (error) return setRenaming((r) => ({ ...r, error }));
    const name = renaming.name.trim();
    if (current && current.name === name) return setRenaming(null);
    setRenaming((r) => ({ ...r, saving: true, error: "" }));
    renameRadiusServer({
      id: renaming.id,
      name,
      onSuccess: () => {
        setServers((list) => list.map((s) => (s.id === renaming.id ? { ...s, name } : s)));
        setRenaming(null);
        msg.success(`Renamed to ${name}.`);
      },
      onFailure: (err) =>
        setRenaming((r) => ({
          ...r,
          saving: false,
          error: err?.response?.status === 409 ? nameTakenMessage(name) : sentence(radiusErrorText(err, "Could not rename the server.")),
        })),
    });
  };

  // ── Delete ──
  const confirmDelete = (s) =>
    modal.confirm({
      title: `Delete ${s.name}?`,
      content: "Its bridge keeps handling logins, but the console stops showing its health and the logs show “Unknown server”.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve) =>
          deleteRadiusServer({
            id: s.id,
            onSuccess: () => {
              msg.success(`${s.name} deleted.`);
              if (detailsId === s.id) setDetailsId(null);
              load(true);
              resolve();
            },
            onFailure: (err) => {
              // 409 while devices still send logins to it: the API's own message.
              msg.error(sentence(radiusErrorText(err, "Could not delete the server.")));
              load(true);
              resolve();
            },
          }),
        ),
    });

  // Menu clicks are rendered in a portal but still bubble through React to the row's onClick.
  const menuItem = (key, label, onClick, extra = {}) => ({
    key,
    label,
    onClick: ({ domEvent }) => {
      domEvent?.stopPropagation();
      onClick();
    },
    ...extra,
  });

  const columns = [
    {
      title: "Server name",
      dataIndex: "name",
      render: (name, s) =>
        renaming?.id === s.id ? (
          <div onClick={(e) => e.stopPropagation()} style={{ minWidth: 0 }}>
            <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
              <Input
                size="small"
                autoFocus
                value={renaming.name}
                maxLength={128}
                status={renaming.error ? "error" : undefined}
                aria-label="Server name"
                onChange={(e) => setRenaming((r) => ({ ...r, name: e.target.value, error: "" }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveRename();
                  if (e.key === "Escape") setRenaming(null);
                }}
              />
              <button type="button" aria-label="Save name" style={{ ...iconButtonStyle(), color: AN_COLORS.success }} onClick={saveRename} disabled={renaming.saving}>
                <Check size={15} />
              </button>
              <button type="button" aria-label="Cancel rename" style={iconButtonStyle()} onClick={() => setRenaming(null)}>
                <X size={15} />
              </button>
            </div>
            <FieldError>{renaming.error}</FieldError>
          </div>
        ) : (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500, color: AN_COLORS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
            <MonoCell>{s.sourceIp}</MonoCell>
          </div>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "7.5rem",
      render: (_, s) => {
        const v = serverStatusView(s);
        return <StatusPill tone={v.tone} label={v.label} />;
      },
    },
    {
      title: "Last heartbeat",
      key: "lastSeenAt",
      width: "10rem",
      render: (_, s) =>
        neverConnected(s) ? (
          <span style={{ color: AN_COLORS.muted }}>Never connected</span>
        ) : (
          <Tooltip title={fmtDate(lastSeenMs(s))}>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{lastHeartbeatText(s, now)}</span>
          </Tooltip>
        ),
    },
    {
      title: "Devices",
      dataIndex: "devicesUsing",
      width: "6rem",
      render: (n) => (
        <Tooltip title="Devices that send logins to this server">
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{Number(n) || 0}</span>
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "actions",
      width: "3.5rem",
      align: "right",
      render: (_, s) => {
        const blocked = deleteBlockedNote(s);
        return (
          <RowActionMenu
            ariaLabel={`Actions for ${s.name}`}
            items={[
              menuItem("details", "View details", () => setDetailsId(s.id)),
              menuItem("env", "Download app.env", () => setSetupFor(s)),
              menuItem("rename", "Rename", () => startRename(s)),
              { type: "divider" },
              menuItem(
                "delete",
                blocked ? (
                  <div style={{ maxWidth: "16rem" }}>
                    <div>Delete</div>
                    <div style={{ fontSize: "0.75rem", whiteSpace: "normal" }}>{blocked}</div>
                  </div>
                ) : (
                  "Delete…"
                ),
                () => confirmDelete(s),
                { danger: !blocked, disabled: !!blocked },
              ),
            ]}
          />
        );
      },
    },
  ];

  const addButton = (
    <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />} onClick={() => setAdding({ server: null })}>
      Add RADIUS server
    </Button>
  );

  let emptyText;
  if (loading) emptyText = "Loading…";
  else if (loadError)
    emptyText = (
      <div style={{ padding: "1.75rem 0.75rem", display: "grid", gap: "0.75rem", justifyItems: "center" }}>
        <span style={{ fontSize: "0.8rem", color: AN_COLORS.muted }}>{loadError}</span>
        <Button icon={<RefreshCw size={13} />} onClick={() => load(false)}>
          Try again
        </Button>
      </div>
    );
  else
    emptyText = (
      <div style={{ padding: "1.75rem 0.75rem", display: "grid", gap: "0.75rem", justifyItems: "center" }}>
        <span style={{ fontSize: "0.8rem", color: AN_COLORS.muted, maxWidth: "30rem" }}>
          No RADIUS servers yet. Add your FreeRADIUS server to start protecting VPN logins.
        </span>
        {addButton}
      </div>
    );

  const closeAdd = () => setAdding(null);

  return (
    <PageShell>
      {msgCtx}
      {modalCtx}
      <PageHeader
        eyebrow="RADIUS"
        title="RADIUS Servers"
        subtitle="FreeRADIUS servers running the AuthNull bridge. Each sends a heartbeat every 60 seconds."
        chips={
          servers.length > 0 && (
            <>
              <CountChip count={servers.length} label={servers.length === 1 ? "Server" : "Servers"} />
              <CountChip count={activeCount} label="Active" />
            </>
          )
        }
        actions={addButton}
      />

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={servers}
          rowKey="id"
          loading={loading && servers.length > 0}
          tableLayout="fixed"
          scroll={{ x: "38rem" }}
          locale={{ emptyText }}
          pagination={servers.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
          onRow={(s) => ({
            onClick: () => renaming?.id !== s.id && setDetailsId(s.id),
            style: { cursor: "pointer" },
          })}
        />
      </TableCard>
      <div style={{ fontSize: "0.78rem", color: AN_COLORS.muted, marginTop: "0.6rem" }}>{STATUS_LEGEND}</div>

      {/* Details */}
      <Drawer title={details?.name || "RADIUS server"} open={!!details} onClose={() => setDetailsId(null)} width={DRAWER_WIDTH} destroyOnClose>
        {details && <ServerDetails server={details} now={now} onDownload={() => setSetupFor(details)} />}
      </Drawer>

      {/* Add RADIUS server, then its setup steps */}
      <Drawer
        title={adding?.server ? `Set up ${adding.server.name}` : "Add RADIUS server"}
        open={!!adding}
        onClose={closeAdd}
        width={DRAWER_WIDTH}
        destroyOnClose
        extra={
          adding?.server && (
            <Button type="primary" onClick={closeAdd}>
              Done
            </Button>
          )
        }
      >
        {adding && !adding.server && (
          <AddServerForm
            servers={servers}
            onCreated={(server) => {
              setAdding({ server });
              msg.success(`${server.name} added.`);
              load(true);
            }}
          />
        )}
        {adding?.server && <RadiusServerSetup server={adding.server} />}
      </Drawer>

      {/* Download app.env for an existing server: the same steps, for that server's id */}
      <Drawer
        title={setupFor ? `app.env for ${setupFor.name}` : "app.env"}
        open={!!setupFor}
        onClose={() => setSetupFor(null)}
        width={DRAWER_WIDTH}
        destroyOnClose
        zIndex={1100}
      >
        {setupFor && <RadiusServerSetup server={setupFor} />}
      </Drawer>
    </PageShell>
  );
}
